/**
 * 驱动页设备会话在线态 API
 * @see docs/device-session-api.md
 */

import axios, { AxiosHeaders, type AxiosInstance } from "axios";

function isDesktopRenderer(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as Window & {
    electronBridge?: unknown;
    electronAPI?: unknown;
  };
  if (w.electronBridge || w.electronAPI) return true;
  return (
    typeof navigator !== "undefined" && /electron/i.test(navigator.userAgent)
  );
}

/**
 * 设备会话 API 基址：
 * - 浏览器：同源 /api
 * - 桌面端 dev：127.0.0.1:8080/api（与浏览器共用 webpack dev hub）
 * - 桌面端打包产物：process.env.API_PUBLIC_ORIGIN（由 BUILD_ENV + BRAND 注入）
 */
function resolveDeviceSessionApiBase(): string {
  if (typeof process !== "undefined" && process.env?.DEVICE_SESSION_HUB) {
    return process.env.DEVICE_SESSION_HUB;
  }

  const buildEnv =
    typeof process !== "undefined" ? process.env?.BUILD_ENV : undefined;

  if (isDesktopRenderer()) {
    if (buildEnv === "dev") {
      const port =
        (typeof process !== "undefined" && process.env?.DEV_SERVER_PORT) ||
        "8080";
      return `http://127.0.0.1:${port}/api`;
    }

    const origin = process.env?.API_PUBLIC_ORIGIN?.replace(/\/$/, "");
    if (origin) {
      return `${origin}/api`;
    }

    if (
      typeof window !== "undefined" &&
      window.location?.origin?.startsWith("http")
    ) {
      return `${window.location.origin}/api`;
    }
  }

  return "/api";
}

const sessionHttp: AxiosInstance = axios.create({
  baseURL: resolveDeviceSessionApiBase(),
  timeout: 10000,
});

sessionHttp.interceptors.request.use((config) => {
  if (!config.headers) {
    config.headers = new AxiosHeaders();
  }
  const token =
    typeof localStorage !== "undefined" ? localStorage.getItem("token") : null;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

sessionHttp.interceptors.response.use(
  (response) => {
    const result = response.data;
    if (result && typeof result === "object" && "success" in result) {
      if (result.success) {
        return result.data;
      }
      return Promise.reject(result);
    }
    return result;
  },
  (error) => Promise.reject(error?.response?.data ?? error)
);

export interface DeviceSessionVO {
  sessionId: string;
  sn: string | null;
  deviceKey: string;
  vid?: string | null;
  pid?: string | null;
  mid?: number | null;
  cid?: number | null;
  deviceType?: string | null;
  solutionProvider?: string | null;
  clientType?: string | null;
  deviceName?: string | null;
  userId?: number | null;
  lastHeartbeatAt: string;
}

export interface DeviceSessionStatusResponse {
  online: boolean;
  sessions: DeviceSessionVO[];
}

export interface HeartbeatParams {
  sessionId: string;
  deviceKey: string;
  sn?: string;
  vid?: string;
  pid?: string;
  mid?: number;
  cid?: number;
  deviceType?: "keyboard" | "mouse";
  solutionProvider?: string;
  clientType?: "web" | "desktop";
  deviceName?: string;
}

/** 客户端推导：有冲突时当前获焦页签为 Owner，否则 Standby */
export interface HeartbeatResponse {
  message?: string;
  role: "owner" | "standby";
  ownerSessionId: string | null;
  ownerClientType?: "web" | "desktop" | null;
}

export type ResolveSessionRoleOptions = {
  /** 默认取 document.hasFocus() */
  hasFocus?: boolean;
};

/** 心跳间隔：与 hub-service SESSION_TTL（90s）成套；拉长以降低 DCDN 请求数费用 */
export const HEARTBEAT_INTERVAL_MS = 30000;
export const CONFLICT_POLL_INTERVAL_MS = 30000;
/** 超过此时间无心跳的远端会话视为僵尸，不参与冲突判定（与服务端 TTL 90s 对齐） */
export const STALE_SESSION_THRESHOLD_MS = 90000;

export function filterLiveDeviceSessions(
  sessions: DeviceSessionVO[],
  nowMs: number = Date.now()
): DeviceSessionVO[] {
  return sessions.filter((s) => {
    const t = Date.parse(s.lastHeartbeatAt);
    if (Number.isNaN(t)) return true;
    return nowMs - t <= STALE_SESSION_THRESHOLD_MS;
  });
}

const DRIVER_SESSION_ID_KEY = "ehub_driver_session_id";

export function getOrCreateDriverSessionId(): string {
  if (typeof sessionStorage === "undefined") {
    return `sess-${Date.now()}`;
  }
  const key = DRIVER_SESSION_ID_KEY;
  let id = sessionStorage.getItem(key);
  if (!id) {
    id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(key, id);
  }
  return id;
}

function normalizeUsbId(value: string | number | null | undefined): string {
  if (value == null) return "";
  const raw = String(value).trim().toLowerCase();
  if (!raw) return "";
  return raw.startsWith("0x") ? raw.slice(2) : raw;
}

/** 同物理设备：SN / deviceKey / VID+PID 任一匹配即视为冲突 */
export function sessionsRepresentSameDevice(
  a: Pick<DeviceSessionVO, "deviceKey" | "sn" | "vid" | "pid">,
  b: Pick<DeviceSessionVO, "deviceKey" | "sn" | "vid" | "pid">
): boolean {
  const snA = String(a.sn ?? "").trim();
  const snB = String(b.sn ?? "").trim();
  if (snA && snB && snA === snB) return true;
  if (a.deviceKey && b.deviceKey && a.deviceKey === b.deviceKey) return true;
  const vidA = normalizeUsbId(a.vid);
  const pidA = normalizeUsbId(a.pid);
  const vidB = normalizeUsbId(b.vid);
  const pidB = normalizeUsbId(b.pid);
  return Boolean(vidA && pidA && vidA === vidB && pidA === pidB);
}

export function findConflictingSessions(
  sessions: DeviceSessionVO[],
  current: {
    sessionId: string;
    sn?: string | null;
    deviceKey: string;
    vid?: string | null;
    pid?: string | null;
  }
): DeviceSessionVO[] {
  return sessions.filter((s) => {
    if (s.sessionId === current.sessionId) return false;
    return sessionsRepresentSameDevice(s, current);
  });
}

export function postHeartbeat(params: HeartbeatParams): Promise<unknown> {
  return sessionHttp.post("/v1/device-sessions/heartbeat", params);
}

export function disconnectSession(sessionId: string): Promise<unknown> {
  return sessionHttp.delete(
    `/v1/device-sessions/${encodeURIComponent(sessionId)}`
  );
}

export function getMyDeviceSessions(): Promise<DeviceSessionStatusResponse> {
  return sessionHttp.get("/v1/device-sessions/me");
}

export function getSessionsBySn(
  sn: string
): Promise<DeviceSessionStatusResponse> {
  return sessionHttp.get("/v1/device-sessions/status", { params: { sn } });
}

export function getSessionsByDeviceIdentity(params: {
  deviceKey?: string;
  sn?: string | null;
  vid?: string | null;
  pid?: string | null;
}): Promise<DeviceSessionStatusResponse> {
  const query: Record<string, string> = {};
  if (params.deviceKey) query.deviceKey = params.deviceKey;
  if (params.sn) query.sn = params.sn;
  if (params.vid) query.vid = params.vid;
  if (params.pid) query.pid = params.pid;
  return sessionHttp.get("/v1/device-sessions/status", { params: query });
}

export type ConflictCheckerContext = {
  sessionId: string;
  deviceKey: string;
  sn?: string | null;
  vid?: string | null;
  pid?: string | null;
  isLoggedIn: boolean;
};

export async function fetchSessionsForConflict(
  ctx: ConflictCheckerContext
): Promise<DeviceSessionVO[]> {
  const mergeBySessionId = (
    ...lists: (DeviceSessionVO[] | undefined)[]
  ): DeviceSessionVO[] => {
    const map = new Map<string, DeviceSessionVO>();
    for (const list of lists) {
      for (const s of list ?? []) {
        map.set(s.sessionId, s);
      }
    }
    return Array.from(map.values());
  };

  const filterSameDevice = (sessions: DeviceSessionVO[]): DeviceSessionVO[] =>
    sessions.filter((s) => sessionsRepresentSameDevice(s, ctx));

  try {
    if (ctx.isLoggedIn) {
      const meRes =
        (await getMyDeviceSessions()) as DeviceSessionStatusResponse;
      const fromMe = meRes.sessions ?? [];
      /** /me 仅含本账号；再按 SN 补齐匿名/他端同设备会话（单次 status，勿叠 deviceKey） */
      if (ctx.sn) {
        const snRes = (await getSessionsBySn(
          ctx.sn
        )) as DeviceSessionStatusResponse;
        return mergeBySessionId(fromMe, snRes.sessions);
      }
      return fromMe;
    }

    /**
     * 未登录：每个同步周期只打一次 status。
     * SN 已能覆盖同设备；勿再叠 deviceKey（hub 对多参是 AND，且与 sn 查询结果重叠）。
     */
    if (ctx.sn) {
      const snRes = (await getSessionsBySn(
        ctx.sn
      )) as DeviceSessionStatusResponse;
      return snRes.sessions ?? [];
    }

    if (ctx.deviceKey || (ctx.vid && ctx.pid)) {
      const idRes = (await getSessionsByDeviceIdentity({
        deviceKey: ctx.deviceKey,
        sn: ctx.sn,
        vid: ctx.vid,
        pid: ctx.pid,
      })) as DeviceSessionStatusResponse;
      return idRes.sessions ?? [];
    }

    /** 无 SN/deviceKey：dev hub /me 返回全部会话，客户端按设备过滤 */
    const meRes = (await getMyDeviceSessions()) as DeviceSessionStatusResponse;
    return filterSameDevice(meRes.sessions ?? []);
  } catch {
    return [];
  }
}

function readHasDocumentFocus(): boolean {
  return typeof document !== "undefined" && document.hasFocus();
}

/** 心跳 POST 后立即 GET status 时，补入本 Tab 会话避免误判双 Standby */
export function mergeSessionsWithOptimisticSelf(
  sessions: DeviceSessionVO[],
  ctx: ConflictCheckerContext,
  heartbeatParams: HeartbeatParams,
  heartbeatAt: string = new Date().toISOString()
): DeviceSessionVO[] {
  const map = new Map<string, DeviceSessionVO>();
  for (const s of sessions) {
    map.set(s.sessionId, s);
  }

  const existing = map.get(ctx.sessionId);
  if (existing) {
    map.set(ctx.sessionId, { ...existing, lastHeartbeatAt: heartbeatAt });
  } else {
    map.set(ctx.sessionId, {
      sessionId: ctx.sessionId,
      sn: ctx.sn ?? heartbeatParams.sn ?? null,
      deviceKey: ctx.deviceKey,
      vid: heartbeatParams.vid ?? null,
      pid: heartbeatParams.pid ?? null,
      mid: heartbeatParams.mid ?? null,
      cid: heartbeatParams.cid ?? null,
      deviceType: heartbeatParams.deviceType ?? null,
      solutionProvider: heartbeatParams.solutionProvider ?? null,
      clientType: heartbeatParams.clientType ?? null,
      deviceName: heartbeatParams.deviceName ?? null,
      userId: null,
      lastHeartbeatAt: heartbeatAt,
    });
  }

  return Array.from(map.values());
}

/** 同设备多会话：获焦窗口 Owner，未获焦 Standby；跨端时活跃方优先 */
export function resolveSessionRole(
  sessions: DeviceSessionVO[],
  ctx: ConflictCheckerContext,
  options?: ResolveSessionRoleOptions
): Pick<HeartbeatResponse, "role" | "ownerSessionId" | "ownerClientType"> {
  const conflicts = findConflictingSessions(sessions, ctx);
  const self = sessions.find((s) => s.sessionId === ctx.sessionId);

  if (conflicts.length === 0) {
    return {
      role: "owner",
      ownerSessionId: ctx.sessionId,
      ownerClientType:
        (self?.clientType as "web" | "desktop" | undefined) ?? null,
    };
  }

  const hasFocus = options?.hasFocus ?? readHasDocumentFocus();
  const selfClientType = self?.clientType ?? null;
  const hasRemoteClientConflict = conflicts.some(
    (c) => c.clientType && selfClientType && c.clientType !== selfClientType
  );

  if (hasRemoteClientConflict) {
    const contenders = [...conflicts, ...(self ? [self] : [])];
    const freshest = [...contenders].sort((a, b) =>
      b.lastHeartbeatAt.localeCompare(a.lastHeartbeatAt)
    )[0];
    const freshestAgeMs = freshest
      ? Date.now() - Date.parse(freshest.lastHeartbeatAt)
      : Number.POSITIVE_INFINITY;
    const remoteIsActive =
      freshest &&
      freshest.clientType !== selfClientType &&
      !Number.isNaN(freshestAgeMs) &&
      freshestAgeMs <= HEARTBEAT_INTERVAL_MS + 2000;

    if (!hasFocus) {
      return {
        role: "standby",
        ownerSessionId: freshest?.sessionId ?? conflicts[0]?.sessionId ?? null,
        ownerClientType:
          (freshest?.clientType as "web" | "desktop" | undefined) ?? null,
      };
    }

    /** Electron 偶发误报获焦：远端浏览器/桌面端仍在活跃心跳时让出 Owner */
    if (
      remoteIsActive &&
      freshest &&
      freshest.sessionId !== ctx.sessionId &&
      freshest.lastHeartbeatAt.localeCompare(self?.lastHeartbeatAt ?? "") > 0
    ) {
      return {
        role: "standby",
        ownerSessionId: freshest.sessionId,
        ownerClientType:
          (freshest.clientType as "web" | "desktop" | undefined) ?? null,
      };
    }
  }

  if (hasFocus) {
    return {
      role: "owner",
      ownerSessionId: ctx.sessionId,
      ownerClientType:
        (self?.clientType as "web" | "desktop" | undefined) ?? null,
    };
  }

  const remoteOwner = [...conflicts].sort((a, b) =>
    a.lastHeartbeatAt.localeCompare(b.lastHeartbeatAt)
  )[0];

  return {
    role: "standby",
    ownerSessionId: remoteOwner?.sessionId ?? conflicts[0]?.sessionId ?? null,
    ownerClientType:
      (remoteOwner?.clientType as "web" | "desktop" | undefined) ?? null,
  };
}

function unwrapHeartbeatMessage(raw: unknown): string {
  if (typeof raw === "string") return raw;
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if (typeof obj.message === "string") return obj.message;
    if (typeof obj.data === "string") return obj.data;
  }
  return "心跳已记录";
}

/** 将 heartbeat 原始响应 + 会话列表归一化为 Owner/Standby */
export function normalizeHeartbeatResponse(
  raw: unknown,
  sessions: DeviceSessionVO[],
  ctx: ConflictCheckerContext,
  options?: ResolveSessionRoleOptions
): HeartbeatResponse {
  const liveSessions = filterLiveDeviceSessions(sessions);
  const resolved = resolveSessionRole(liveSessions, ctx, options);

  if (raw && typeof raw === "object" && "role" in (raw as object)) {
    const r = raw as HeartbeatResponse;
    const liveConflicts = findConflictingSessions(liveSessions, ctx);
    /** 服务端仍返回 standby 但僵尸会话已剔除且无冲突 → 以客户端为准升为 owner */
    if (r.role === "standby" && liveConflicts.length === 0) {
      return {
        message: r.message ?? unwrapHeartbeatMessage(raw),
        ...resolved,
      };
    }
    /** 服务端 standby 但本端获焦且仍有存活冲突 → 获焦者优先 */
    if (
      r.role === "standby" &&
      resolved.role === "owner" &&
      liveConflicts.length > 0
    ) {
      return {
        message: r.message ?? unwrapHeartbeatMessage(raw),
        ...resolved,
      };
    }
    /** 仍有存活冲突时以客户端 focus 判定为准（避免双 Owner、旁观提示不显示） */
    if (liveConflicts.length > 0) {
      return {
        message: r.message ?? unwrapHeartbeatMessage(raw),
        ...resolved,
      };
    }
    return {
      message: r.message,
      role: r.role,
      ownerSessionId: r.ownerSessionId,
      ownerClientType: r.ownerClientType ?? null,
    };
  }

  return {
    message: unwrapHeartbeatMessage(raw),
    ...resolved,
  };
}
